/** Cotizador en pantalla unica: compone un requerimiento nuevo (design.md §22.5). */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileButton } from '@/components/MobileButton';
import { MobileIcon } from '@/components/MobileIcon';
import { MobileInput, MobileTextarea } from '@/components/MobileInput';
import { ClientAutocomplete } from '@/components/ClientAutocomplete';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { QuantityStepper } from '@/components/QuantityStepper';
import { SearchInput } from '@/components/SearchInput';
import { SectionCard } from '@/components/SectionCard';
import { CardListSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import { useMobileClientsQuery } from '@/hooks/useMobileClients';
import {
  useCatalogCompositionQuery,
  useComposeRequirementMutation,
  useMobileCatalogQuery,
  useSimulateQuoteMutation,
  type CatalogGroup,
} from '@/hooks/useMobileQuotations';
import {
  mobileQuoteGeneratorSchema,
  type MobileQuoteGeneratorForm,
} from '@/schemas/quote-generator.schemas';
import { extractErrorMessage } from '@/services/mobileApiClient';
import type { CompositionNode } from '@/services/commercialService';
import { formatCurrency } from '@/utils/format';
import { hapticSuccess } from '@/utils/haptic';

const CATALOG_GROUPS = [
  { value: 'sale', label: 'Partidas técnicas' },
  { value: 'additional', label: 'Artículos de venta' },
] as const;

function todayIso(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatTotal(simulation: unknown): number | null {
  if (typeof simulation !== 'object' || simulation === null) return null;
  const record = simulation as Record<string, unknown>;
  for (const key of ['total_amount', 'total', 'grand_total']) {
    const parsed = toNumber(record[key]);
    if (parsed !== null) return parsed;
  }
  return null;
}

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function CompositionChildren({ nodes, depth }: { nodes: CompositionNode[]; depth: number }) {
  if (nodes.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1.5">
      {nodes.map((child, index) => {
        const key = child.master_item_id ?? child.code ?? `nodo-${depth}-${index}`;
        const quantity = toNumber(child.quantity);
        const meta =
          [child.code, quantity !== null ? `Cantidad ${quantity}` : null]
            .filter((part) => part !== null)
            .join(' · ') || '—';
        return (
          <li key={key} className="rounded-lg bg-white px-3 py-2">
            <p className="break-words text-sm font-medium text-text-primary">
              {child.name ?? child.code ?? 'Componente'}
            </p>
            <p title={meta} className="mt-0.5 truncate text-xs text-text-tertiary">{meta}</p>
            <CompositionChildren nodes={child.children ?? []} depth={depth + 1} />
          </li>
        );
      })}
    </ul>
  );
}

function CompositionContent({ itemId, quantity }: { itemId: string; quantity: number }) {
  const compositionQuery = useCatalogCompositionQuery(itemId, quantity);
  if (compositionQuery.isPending) {
    return <p className="mt-2 truncate text-sm text-text-tertiary">Cargando contenido…</p>;
  }
  if (compositionQuery.isError) {
    return (
      <div className="mt-2 flex items-center gap-2">
        <MobileIcon name="error" className="h-4 w-4 shrink-0 text-error" />
        <p className="min-w-0 flex-1 truncate text-sm text-error">No se pudo cargar el contenido.</p>
        <button
          type="button"
          onClick={() => void compositionQuery.refetch()}
          className="flex min-h-[44px] shrink-0 items-center rounded-lg px-2 text-sm font-semibold text-error focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Reintentar
        </button>
      </div>
    );
  }
  const children = compositionQuery.data?.children ?? [];
  if (children.length === 0) {
    return <p className="mt-2 truncate text-sm text-text-tertiary">Sin componentes.</p>;
  }
  return <CompositionChildren nodes={children} depth={0} />;
}

export function QuoteGeneratorPage() {
  const navigate = useNavigate();
  const [itemSearch, setItemSearch] = useState('');
  const [group, setGroup] = useState<CatalogGroup>('sale');
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSummary, setConfirmSummary] = useState('');
  const [pendingBody, setPendingBody] = useState<Record<string, unknown> | null>(null);
  const catalogQuery = useMobileCatalogQuery(itemSearch, group);
  const clientsQuery = useMobileClientsQuery();
  const simulateMutation = useSimulateQuoteMutation();
  const composeMutation = useComposeRequirementMutation();
  const {
    register,
    control,
    watch,
    setValue,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<MobileQuoteGeneratorForm>({
    resolver: yupResolver(mobileQuoteGeneratorSchema),
    defaultValues: {
      client_id: '',
      new_requirement_name: '',
      new_requirement_required_date: '',
      new_requirement_description: '',
      lines: [],
      discount_percent: 0,
      discount_reason: '',
    },
  });

  const clientId = watch('client_id') ?? '';
  const discountPercent = watch('discount_percent') ?? 0;
  const items = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);

  const currentLines = useMemo(
    () =>
      Object.entries(quantities)
        .filter(([, qty]) => qty > 0)
        .map(([id, qty]) => ({ master_item_id: id, quantity: qty })),
    [quantities],
  );
  const totalItems = useMemo(
    () => currentLines.reduce((sum, line) => sum + line.quantity, 0),
    [currentLines],
  );
  const hasLines = currentLines.length > 0;
  const busy = simulateMutation.isPending || composeMutation.isPending;

  useEffect(() => {
    setValue('lines', currentLines, { shouldValidate: false });
  }, [currentLines, setValue]);

  const clientName = useMemo(
    () => clientsQuery.data?.items.find((client) => client.id === clientId)?.legal_name ?? '',
    [clientsQuery.data, clientId],
  );

  const estimatedTotal = useMemo(() => {
    let sum = 0;
    for (const line of currentLines) {
      const item = items.find((entry) => entry.id === line.master_item_id);
      if (item && typeof item.price === 'number') sum += item.price * line.quantity;
    }
    return sum > 0 ? sum : null;
  }, [currentLines, items]);

  function pickGroup(next: CatalogGroup) {
    if (next === group) return;
    setGroup(next);
    setItemSearch('');
  }

  async function simulate() {
    setGeneralError(null);
    try {
      if (!hasLines) throw new Error('Agrega al menos una partida');
      const discount = Number(discountPercent) || 0;
      await simulateMutation.mutateAsync({ lines: currentLines, discount_percent: discount });
    } catch (err) {
      setGeneralError(extractErrorMessage(err));
    }
  }

  async function onSubmit(values: MobileQuoteGeneratorForm) {
    setGeneralError(null);
    const discount = Number(values.discount_percent) || 0;
    const description = values.new_requirement_description?.trim() ?? '';
    const body: Record<string, unknown> = {
      client_id: values.client_id,
      new_requirement: {
        name: values.new_requirement_name.trim(),
        required_date: values.new_requirement_required_date,
        ...(description !== '' ? { description } : {}),
      },
      lines: values.lines,
      discount_percent: discount,
    };
    if (discount > 0) body['discount_reason'] = values.discount_reason?.trim();
    setPendingBody(body);
    const total = simulateMutation.data ? formatTotal(simulateMutation.data) : estimatedTotal;
    const summary = [
      `Cliente: ${clientName || 'seleccionado'}`,
      `Requerimiento: ${values.new_requirement_name.trim()} (${formatDate(values.new_requirement_required_date)})`,
      `${values.lines.length} partida${values.lines.length !== 1 ? 's' : ''}`,
      `Total: ${total !== null ? formatCurrency(total) : 'sin simular'}`,
    ].join(' · ');
    setConfirmSummary(summary);
    setConfirmOpen(true);
  }

  async function confirmCompose() {
    if (!pendingBody) return;
    setGeneralError(null);
    try {
      const created = await composeMutation.mutateAsync(pendingBody);
      setQuantities({});
      simulateMutation.reset();
      reset();
      setItemSearch('');
      setGroup('sale');
      setExpandedId(null);
      setPendingBody(null);
      setConfirmOpen(false);
      hapticSuccess();
      toast.success(`Requerimiento ${created.code || created.id} creado`);
      navigate(`/m/requirements/${created.id}`, { replace: true });
    } catch (err) {
      setConfirmOpen(false);
      setGeneralError(extractErrorMessage(err));
    }
  }

  const serverTotal = simulateMutation.data ? formatTotal(simulateMutation.data) : null;
  const displayTotal = serverTotal ?? estimatedTotal;

  return (
    <MobileLayout title="Cotizador" subtitle={`${currentLines.length} partidas con cantidad`}>
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4 pb-32" noValidate>
        <SectionCard title="Cliente y requerimiento nuevo">
          <div className="space-y-4">
            <Controller
              control={control}
              name="client_id"
              render={({ field }) => (
                <ClientAutocomplete
                  value={field.value || null}
                  onChange={(next) => field.onChange(next ?? '')}
                  label="Cliente"
                  isRequired
                  disabled={busy}
                  error={errors.client_id?.message}
                />
              )}
            />

            <MobileInput
              label="Nombre del requerimiento"
              placeholder="Ej: Servicio de mantenimiento"
              enterKeyHint="next"
              staticLabel
              isRequired
              icon={<MobileIcon name="title" className="h-5 w-5" />}
              error={errors.new_requirement_name?.message}
              {...register('new_requirement_name')}
            />
            <MobileInput
              label="Fecha requerida"
              type="date"
              staticLabel
              isRequired
              min={todayIso()}
              icon={<MobileIcon name="schedule" className="h-5 w-5" />}
              error={errors.new_requirement_required_date?.message}
              {...register('new_requirement_required_date')}
            />
            <MobileTextarea
              label="Descripción (opcional)"
              placeholder="Detalle adicional del requerimiento..."
              rows={3}
              maxLength={5000}
              error={errors.new_requirement_description?.message}
              {...register('new_requirement_description')}
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Partidas"
          subtitle={group === 'sale' ? 'Partidas técnicas' : 'Artículos de venta'}
        >
          <div
            role="radiogroup"
            aria-label="Grupo del catálogo"
            className="mb-3 inline-flex w-full gap-1 rounded-full bg-bg-tertiary p-1"
          >
            {CATALOG_GROUPS.map((option) => {
              const active = group === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={busy}
                  onClick={() => pickGroup(option.value)}
                  className={clsx(
                    'flex min-h-[44px] flex-1 items-center justify-center rounded-full px-3 text-sm font-semibold transition-colors disabled:opacity-40',
                    active ? 'bg-white text-primary shadow-card' : 'text-text-secondary',
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <SearchInput
            value={itemSearch}
            onChange={setItemSearch}
            placeholder="Buscar por código o nombre..."
          />
          {itemSearch.trim() !== '' && itemSearch.trim().length < 2 && (
            <p className="mt-1.5 break-words text-sm text-text-tertiary">
              Escribe al menos 2 letras para buscar en el servidor.
            </p>
          )}
          {errors.lines?.message && (
            <p className="mt-1.5 flex items-center gap-1 text-sm text-error">
              <MobileIcon name="error" className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1 break-words">{errors.lines.message as string}</span>
            </p>
          )}
          <div className="mt-3 space-y-3">
            {catalogQuery.isPending && <CardListSkeleton count={3} />}
            {catalogQuery.isError && (
              <EmptyState
                icon="error"
                title="No se pudo cargar el catálogo"
                description="Verifica tu conexión e intenta de nuevo."
                actionLabel="Reintentar"
                onAction={() => catalogQuery.refetch()}
              />
            )}
            {!catalogQuery.isPending && !catalogQuery.isError && items.length === 0 && (
              <EmptyState
                icon="inventory_2"
                title="Sin items"
                description={
                  itemSearch.trim().length >= 2
                    ? 'Ninguno coincide con tu búsqueda.'
                    : 'No hay ítems ofertables disponibles.'
                }
              />
            )}
            {items.map((item) => {
              const qty = quantities[item.id] ?? 0;
              const expanded = expandedId === item.id;
              return (
                <div
                  key={item.id}
                  className={clsx(
                    'rounded-xl p-4 transition-colors',
                    qty > 0 ? 'bg-primary-light ring-1 ring-primary' : 'bg-bg-tertiary',
                  )}
                >
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="break-words font-semibold text-text-primary">{item.name}</p>
                      <p title={item.code} className="mt-0.5 truncate text-xs text-text-tertiary">
                        {item.code}
                        {item.unit_code ? ` · ${item.unit_code}` : ''}
                      </p>
                      {item.price !== undefined && (
                        <p className="mt-0.5 truncate text-sm text-text-secondary">
                          {formatCurrency(item.price, item.currency_code ?? 'PEN')}
                        </p>
                      )}
                    </div>
                    {qty > 0 && (
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                        {qty}
                      </span>
                    )}
                  </div>
                  <QuantityStepper
                    label={item.name}
                    value={qty}
                    onChange={(next) => setQuantities((prev) => ({ ...prev, [item.id]: next }))}
                  />
                  {item.has_composition && (
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() => setExpandedId(expanded ? null : item.id)}
                        aria-expanded={expanded}
                        className="flex min-h-[44px] items-center gap-1 text-sm font-semibold text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <MobileIcon
                          name={expanded ? 'chevron_down' : 'chevron_right'}
                          className="h-5 w-5"
                        />
                        {expanded ? 'Ocultar contenido' : 'Ver contenido'}
                      </button>
                      {expanded && (
                        <CompositionContent itemId={item.id} quantity={qty > 0 ? qty : 1} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </SectionCard>

        <SectionCard title="Descuento">
          <div className="space-y-3">
            <MobileInput
              label="Descuento %"
              type="text"
              inputMode="decimal"
              placeholder="0"
              staticLabel
              icon={<MobileIcon name="calculate" className="h-5 w-5" />}
              error={errors.discount_percent?.message}
              {...register('discount_percent')}
            />
            <MobileInput
              label={`Motivo del descuento${Number(discountPercent) > 0 ? ' *' : ''}`}
              placeholder="Ej: Pronto pago"
              enterKeyHint="done"
              staticLabel
              icon={<MobileIcon name="edit_note" className="h-5 w-5" />}
              error={errors.discount_reason?.message}
              {...register('discount_reason')}
            />
          </div>
        </SectionCard>

        {generalError && (
          <div className="flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3">
            <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
            <p className="min-w-0 flex-1 break-words text-sm text-error">{generalError}</p>
          </div>
        )}
      </form>

      {items.length > 0 && (
        <div className="fixed inset-x-0 bottom-[76px] z-30 rounded-t-card border-t border-border-light bg-white shadow-card safe-area-bottom">
          <div className="space-y-3 px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-sm text-text-secondary">
                {totalItems} item{totalItems !== 1 ? 's' : ''} seleccionado{totalItems !== 1 ? 's' : ''}
              </span>
              {displayTotal !== null ? (
                <span className="shrink-0 text-xl font-bold tabular-nums text-primary">
                  {formatCurrency(displayTotal)}
                </span>
              ) : (
                <span className="shrink-0 text-sm text-text-tertiary">Sin total</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <MobileButton
                variant="secondary"
                size="md"
                disabled={busy || !hasLines}
                loading={simulateMutation.isPending}
                onClick={() => void simulate()}
                icon={<MobileIcon name="calculate" className="h-5 w-5" />}
              >
                Simular
              </MobileButton>
              <MobileButton
                variant="primary"
                size="md"
                disabled={busy}
                loading={composeMutation.isPending}
                onClick={() => void handleSubmit(onSubmit)()}
                icon={<MobileIcon name="save" className="h-5 w-5" />}
              >
                Guardar
              </MobileButton>
            </div>
          </div>
        </div>
      )}

      {confirmOpen && (
        <ConfirmDialog
          title="Guardar composición"
          message={confirmSummary}
          confirmLabel="Componer requerimiento"
          busy={composeMutation.isPending}
          onConfirm={() => void confirmCompose()}
          onCancel={() => {
            setConfirmOpen(false);
            setPendingBody(null);
          }}
        />
      )}
    </MobileLayout>
  );
}
